import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ZoomOut } from 'lucide-react'
import {
  EncounterAbilityRow,
  PhaseBoundaryHandle,
  PhaseDivider,
} from '../hooks/useTeamPlanComputedData'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useObservedWidth from '../hooks/useObservedWidth'
import useTimelineTicks from '../hooks/useTimelineTicks'
import useVisibleTimelineTicks from '../hooks/useVisibleTimelineTicks'
import { formatTimelineTimestamp } from './timelineMath'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import useTimelineZoomInteraction from '../hooks/useTimelineZoomInteraction'
import TimelineDragSelectionOverlay from './TimelineDragSelectionOverlay'

type TimelineContainerProps = {
  maxTime: number
  phaseDividers: PhaseDivider[]
  phaseBoundaryHandles: PhaseBoundaryHandle[]
  rows: EncounterAbilityRow[]
  hoverTime?: number | null
  onPhaseBoundaryDrag?: (boundaryIndex: number, nextAt: number) => void
  onPhaseBoundaryDragEnd?: (boundaryIndex: number, nextAt: number) => void
  disableZoomInteraction?: boolean
  castMarkers?: {
    id: string
    at: number
    label: string
    ignored?: boolean
    tooltip: string
    disableTooltip?: boolean
    onPointerDown?: (event: React.PointerEvent<SVGRectElement>) => void
    onClick?: (event: React.MouseEvent<SVGRectElement>) => void
    onContextMenu?: (event: React.MouseEvent<SVGRectElement>) => void
  }[]
  enableAddCastInteraction?: boolean
  onAddCastAt?: (at: number) => void
  emptyMessage?: string
  className?: string
}

const ROW_HEIGHT = 20
const ROW_GAP = 0
const PHASE_LABEL_AREA_HEIGHT = 14
const TICK_LABEL_AREA_HEIGHT = 20
const EMPTY_STATE_ROWS_HEIGHT = 128
const PHASE_CARD_GAP_PX = 4
const EDGE_LABEL_COLLISION_PADDING_SECONDS = 8
const CAST_LABEL_MIN_GAP_SECONDS = 7
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

type ZoomEdgeControlProps = {
  x: number
  y: number
  width: number
  label: string
  onReset: (event: React.MouseEvent | React.PointerEvent) => void
}

const ZoomEdgeControl = ({
  x,
  y,
  width,
  label,
  onReset,
}: ZoomEdgeControlProps) => {
  return (
    <foreignObject x={x} y={y - 2} width={width} height={20}>
      <button
        type='button'
        className='h-4 w-full rounded-sm px-1 bg-info text-info-foreground border border-info-foreground transition-colors flex items-center gap-1 text-[12px] leading-none'
        onPointerDown={onReset}
        onClick={onReset}
      >
        <span className='truncate'>{label}</span>
        <ZoomOut className='w-3.5 h-3.5 shrink-0 ml-auto' />
      </button>
    </foreignObject>
  )
}

const TimelineContainer = ({
  maxTime,
  phaseDividers,
  phaseBoundaryHandles,
  rows,
  hoverTime,
  onPhaseBoundaryDrag,
  onPhaseBoundaryDragEnd,
  disableZoomInteraction = false,
  castMarkers = [],
  enableAddCastInteraction = false,
  onAddCastAt,
  emptyMessage,
  className,
}: TimelineContainerProps) => {
  const { zoom, setZoom } = useTeamPlanTimeline()
  const rootRef = useRef<HTMLDivElement>(null)
  const phaseBoundaryHandlesRef = useRef(phaseBoundaryHandles)
  const visibleRangeRef = useRef({
    start: 0,
    end: maxTime,
  })
  const timelineWidth = useObservedWidth(rootRef)
  const [addHoverTime, setAddHoverTime] = useState<number | null>(null)
  const [addHoverBlocked, setAddHoverBlocked] = useState(false)

  const visibleRange = useMemo(() => {
    const start = clamp(zoom?.start ?? 0, 0, maxTime)
    const end = clamp(zoom?.end ?? maxTime, start + 1, maxTime)
    return { start, end }
  }, [maxTime, zoom?.end, zoom?.start])
  const isZoomed = visibleRange.start > 0 || visibleRange.end < maxTime

  useEffect(() => {
    phaseBoundaryHandlesRef.current = phaseBoundaryHandles
  }, [phaseBoundaryHandles])

  useEffect(() => {
    visibleRangeRef.current = visibleRange
  }, [visibleRange])

  const toBoundaryTime = useCallback(
    (clientX: number, rect: DOMRect, boundaryIndex: number) => {
      const handle = phaseBoundaryHandlesRef.current[boundaryIndex]
      if (!handle) return 0
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1)
      const rawAt =
        visibleRangeRef.current.start +
        ratio * (visibleRangeRef.current.end - visibleRangeRef.current.start)
      const clampedAt = Math.max(handle.min, Math.min(handle.max, rawAt))
      return Math.floor(clampedAt)
    },
    [],
  )

  const onBoundaryHandlePointerDown = useCallback(
    (event: React.PointerEvent<SVGRectElement>, boundaryIndex: number) => {
      const svg = event.currentTarget.ownerSVGElement
      if (!svg) return
      event.preventDefault()
      event.stopPropagation()

      const rect = svg.getBoundingClientRect()
      const onMove = (moveEvent: PointerEvent) => {
        const nextAt = toBoundaryTime(moveEvent.clientX, rect, boundaryIndex)
        onPhaseBoundaryDrag?.(boundaryIndex, nextAt)
      }

      const onUp = (upEvent: PointerEvent) => {
        const nextAt = toBoundaryTime(upEvent.clientX, rect, boundaryIndex)
        onPhaseBoundaryDragEnd?.(boundaryIndex, nextAt)
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [onPhaseBoundaryDrag, onPhaseBoundaryDragEnd, toBoundaryTime],
  )
  const { minorTickTimes, minuteTickTimes } = useTimelineTicks({
    maxTime,
    phaseDividers,
  })

  const minuteTickLabelTimes = useMemo(
    () =>
      minuteTickTimes.filter(
        (tickAt) =>
          tickAt + 15 <= maxTime &&
          tickAt >= visibleRange.start &&
          tickAt <= visibleRange.end &&
          (!isZoomed ||
            (Math.abs(tickAt - visibleRange.start) >
              EDGE_LABEL_COLLISION_PADDING_SECONDS &&
              Math.abs(tickAt - visibleRange.end) >
                EDGE_LABEL_COLLISION_PADDING_SECONDS)),
      ),
    [isZoomed, maxTime, minuteTickTimes, visibleRange.end, visibleRange.start],
  )

  const phaseRanges = useMemo(() => {
    let start = 0
    return phaseDividers.map((phase, phaseIndex) => {
      const isLastPhase = phaseIndex === phaseDividers.length - 1
      const end = isLastPhase
        ? maxTime
        : Math.max(start, Math.min(phase.at, maxTime))
      const range = { name: phase.name, start, end }
      start = end
      return range
    })
  }, [maxTime, phaseDividers])

  const visiblePhaseRanges = useMemo(
    () =>
      phaseRanges
        .map((phase) => ({
          ...phase,
          visibleStart: Math.max(phase.start, visibleRange.start),
          visibleEnd: Math.min(phase.end, visibleRange.end),
        }))
        .filter((phase) => phase.visibleEnd > phase.visibleStart),
    [phaseRanges, visibleRange.end, visibleRange.start],
  )

  const rowsHeight = Math.max(
    rows.length * ROW_HEIGHT + Math.max(rows.length - 1, 0) * ROW_GAP,
    emptyMessage ? EMPTY_STATE_ROWS_HEIGHT : 0,
  )
  const rowsTop = PHASE_LABEL_AREA_HEIGHT
  const svgHeight = rowsTop + rowsHeight + TICK_LABEL_AREA_HEIGHT

  const onDragZoom = useCallback(
    (range: { start: number; end: number }) => {
      const start = Math.floor(clamp(range.start, 0, maxTime))
      const end = Math.floor(clamp(range.end, start + 1, maxTime))
      if (end - start < 1) return
      if (start <= 0 && end >= maxTime) {
        setZoom(null)
        return
      }
      setZoom({ start, end })
    },
    [maxTime, setZoom],
  )

  const {
    toX,
    selectionPixels,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  } = useTimelineZoomInteraction({
    maxTime,
    visibleRange,
    timelineWidth,
    disabled: isZoomed || disableZoomInteraction,
    onZoom: onDragZoom,
  })

  const toWidth = useCallback(
    (duration: number) => {
      const safeDuration = Math.max(duration, 0)
      const visibleDuration = Math.max(visibleRange.end - visibleRange.start, 1)
      return Math.max((safeDuration / visibleDuration) * timelineWidth, 1)
    },
    [timelineWidth, visibleRange.end, visibleRange.start],
  )

  const { visibleMinorTickTimes, visibleMinuteTickTimes } =
    useVisibleTimelineTicks({
      minorTickTimes,
      minuteTickTimes,
      visibleRange,
    })
  const orderedCastMarkers = useMemo(
    () =>
      [...castMarkers].sort((left, right) => {
        if (left.ignored === right.ignored) return 0
        return left.ignored ? -1 : 1
      }),
    [castMarkers],
  )
  const markerXPositions = useMemo(
    () => orderedCastMarkers.map((marker) => toX(marker.at)),
    [orderedCastMarkers, toX],
  )

  const hoverTimestamp =
    hoverTime !== null &&
    hoverTime !== undefined &&
    hoverTime >= visibleRange.start &&
    hoverTime <= visibleRange.end
      ? formatTimelineTimestamp(hoverTime)
      : null
  const hoverTimestampWidth = hoverTimestamp ? hoverTimestamp.length * 6 + 8 : 0
  const hoverTimestampX =
    hoverTime !== null && hoverTime !== undefined
      ? Math.max(
          hoverTimestampWidth / 2,
          Math.min(toX(hoverTime), timelineWidth - hoverTimestampWidth / 2),
        )
      : 0
  const addHoverTimestamp =
    addHoverTime !== null ? formatTimelineTimestamp(addHoverTime) : null
  const addHoverTimestampWidth = addHoverTimestamp
    ? addHoverTimestamp.length * 6 + 8
    : 0
  const addHoverTimestampX =
    addHoverTime !== null
      ? Math.max(
          addHoverTimestampWidth / 2,
          Math.min(
            toX(addHoverTime),
            timelineWidth - addHoverTimestampWidth / 2,
          ),
        )
      : 0
  const addHoverActionLabel = '+'
  const addHoverActionLabelWidth = addHoverActionLabel.length * 6 + 8
  const addHoverActionLabelX =
    addHoverTime !== null
      ? Math.max(
          addHoverActionLabelWidth / 2,
          Math.min(
            toX(addHoverTime),
            timelineWidth - addHoverActionLabelWidth / 2,
          ),
        )
      : 0
  const rangeStartLabel = formatTimelineTimestamp(visibleRange.start)
  const rangeEndLabel = formatTimelineTimestamp(visibleRange.end)
  const tickLabelY = rowsTop + rowsHeight + 14
  const startEdgeIsBound = visibleRange.start <= 0
  const endEdgeIsBound = visibleRange.end >= maxTime
  const startControlWidth = rangeStartLabel.length * 6 + 40
  const endControlWidth = rangeEndLabel.length * 6 + 40
  const edgeControlY = tickLabelY - 11

  const handleResetStartEdge = useCallback(
    (event: React.MouseEvent | React.PointerEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setZoom(null)
    },
    [setZoom],
  )

  const handleResetEndEdge = useCallback(
    (event: React.MouseEvent | React.PointerEvent) => {
      event.preventDefault()
      event.stopPropagation()
      setZoom(null)
    },
    [setZoom],
  )

  const toTime = useCallback(
    (clientX: number, rect: DOMRect) => {
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1)
      const nextTime =
        visibleRange.start + ratio * (visibleRange.end - visibleRange.start)
      return Math.floor(clamp(nextTime, 0, maxTime))
    },
    [maxTime, visibleRange.end, visibleRange.start],
  )

  const onAddSurfacePointerMove = useCallback(
    (event: React.PointerEvent<SVGRectElement>) => {
      if (!enableAddCastInteraction) return
      const rect = event.currentTarget.getBoundingClientRect()
      if (rect.width <= 0) {
        setAddHoverTime(null)
        setAddHoverBlocked(false)
        return
      }
      const nextTime = toTime(event.clientX, rect)
      const nextX = toX(nextTime)
      const blocked = markerXPositions.some(
        (markerX) => Math.abs(markerX - nextX) <= 8,
      )
      setAddHoverTime(nextTime)
      setAddHoverBlocked(blocked)
    },
    [enableAddCastInteraction, markerXPositions, toTime, toX],
  )

  const onAddSurfacePointerLeave = useCallback(() => {
    setAddHoverTime(null)
    setAddHoverBlocked(false)
  }, [])

  const onAddSurfaceClick = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      if (!enableAddCastInteraction || !onAddCastAt) return
      if (addHoverBlocked || addHoverTime === null) return
      event.preventDefault()
      event.stopPropagation()
      onAddCastAt(addHoverTime)
    },
    [addHoverBlocked, addHoverTime, enableAddCastInteraction, onAddCastAt],
  )

  return (
    <div className='w-full' ref={rootRef}>
      <svg
        className='block w-full select-none'
        width='100%'
        height={svgHeight}
        viewBox={`0 0 ${timelineWidth} ${svgHeight}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <g>
          {visiblePhaseRanges.length > 0 ? (
            visiblePhaseRanges.map((phase, index) => {
              const xStart = toX(phase.visibleStart)
              const xEnd = toX(phase.visibleEnd)
              const phaseWidth = Math.max(0, xEnd - xStart)
              const leftInset = index === 0 ? 0 : PHASE_CARD_GAP_PX / 2
              const rightInset =
                index === visiblePhaseRanges.length - 1
                  ? 0
                  : PHASE_CARD_GAP_PX / 2
              const x = xStart + leftInset
              const width = Math.max(0, phaseWidth - leftInset - rightInset)
              return (
                <g
                  key={`${phase.name}-${phase.start}-${phase.end}`}
                  className='group'
                >
                  <g className={className}>
                    <rect
                      x={x}
                      y={rowsTop}
                      width={width}
                      height={rowsHeight}
                      fill='currentColor'
                      rx={4}
                      ry={4}
                    />
                  </g>
                  <g className='text-border'>
                    <rect
                      x={x}
                      y={rowsTop}
                      width={width}
                      height={rowsHeight}
                      fill='transparent'
                      stroke='currentColor'
                      strokeWidth={1.25}
                      vectorEffect='non-scaling-stroke'
                      rx={8}
                      ry={8}
                    />
                  </g>
                  <g className='text-foreground'>
                    <text
                      x={x + width / 2}
                      y={9}
                      fill='currentColor'
                      fontSize={12}
                      textAnchor='middle'
                    >
                      {phase.name}
                    </text>
                  </g>
                </g>
              )
            })
          ) : (
            <g>
              <g className={className}>
                <rect
                  x={0}
                  y={rowsTop}
                  width={Math.max(timelineWidth, 1)}
                  height={rowsHeight}
                  fill='currentColor'
                  rx={4}
                  ry={4}
                />
              </g>
              <g className='text-border'>
                <rect
                  x={0}
                  y={rowsTop}
                  width={Math.max(timelineWidth, 1)}
                  height={rowsHeight}
                  fill='transparent'
                  stroke='currentColor'
                  strokeWidth={1.25}
                  vectorEffect='non-scaling-stroke'
                  rx={8}
                  ry={8}
                />
              </g>
            </g>
          )}
        </g>

        <g className='text-border'>
          {visibleMinorTickTimes.map((tickAt) => {
            const x = toX(tickAt)
            return (
              <line
                key={`minor-tick-${tickAt}`}
                x1={x}
                x2={x}
                y1={rowsTop}
                y2={rowsTop + rowsHeight}
                stroke='currentColor'
                strokeWidth={1}
                opacity={0.45}
              />
            )
          })}
        </g>

        <g className='text-foreground'>
          {visibleMinuteTickTimes.map((tickAt) => {
            const x = toX(tickAt)
            return (
              <line
                key={`minute-tick-${tickAt}`}
                x1={x}
                x2={x}
                y1={rowsTop}
                y2={rowsTop + rowsHeight}
                stroke='currentColor'
                strokeWidth={1}
                opacity={0.3}
              />
            )
          })}
        </g>

        <g>
          {onPhaseBoundaryDrag && onPhaseBoundaryDragEnd
            ? phaseBoundaryHandles.map((handle) => {
                if (
                  handle.at < visibleRange.start ||
                  handle.at > visibleRange.end
                ) {
                  return null
                }
                const x = toX(handle.at)
                return (
                  <g key={`phase-boundary-handle-${handle.index}`}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <rect
                          x={x - 5}
                          y={rowsTop}
                          width={10}
                          height={rowsHeight}
                          fill='transparent'
                          className='cursor-col-resize'
                          onPointerDown={(event) =>
                            onBoundaryHandlePointerDown(event, handle.index)
                          }
                        />
                      </TooltipTrigger>
                      <TooltipContent>Phase can be resized</TooltipContent>
                    </Tooltip>
                  </g>
                )
              })
            : null}
        </g>

        {emptyMessage && rows.length === 0 ? (
          <foreignObject
            x={0}
            y={rowsTop}
            width={Math.max(timelineWidth, 1)}
            height={rowsHeight}
          >
            <div className='h-full w-full flex items-center justify-center px-4'>
              <p className='text-center text-muted-foreground text-sm'>
                {emptyMessage}
              </p>
            </div>
          </foreignObject>
        ) : null}

        {rows.map((row, rowIndex) => {
          const y = rowsTop + rowIndex * (ROW_HEIGHT + ROW_GAP)
          const rowEntries = row.entries
            .map((entry, entryIndex) => {
              const entryStart = entry.time
              const entryEnd = entry.time + entry.duration
              const visibleStart = Math.max(entryStart, visibleRange.start)
              const visibleEnd = Math.min(entryEnd, visibleRange.end)
              if (visibleEnd <= visibleStart) return null
              const previousEntry = row.entries[entryIndex - 1]
              const nextEntry = row.entries[entryIndex + 1]
              const showLabel =
                !previousEntry ||
                entry.time - previousEntry.time > CAST_LABEL_MIN_GAP_SECONDS
              const hasHiddenFollower =
                Boolean(nextEntry) &&
                nextEntry.time - entry.time <= CAST_LABEL_MIN_GAP_SECONDS
              return {
                key: `${row.spellId}-${entryIndex}`,
                x: toX(visibleStart),
                width: toWidth(visibleEnd - visibleStart),
                label: formatTimelineTimestamp(entry.time),
                showLabel,
                hasHiddenFollower,
              }
            })
            .filter((entry): entry is NonNullable<typeof entry> =>
              Boolean(entry),
            )

          return (
            <g key={`${row.spellId}-${row.name}`}>
              {rowEntries.map((entry) => {
                return (
                  <g key={entry.key}>
                    <g className='text-border'>
                      <rect
                        x={entry.x}
                        y={y}
                        width={entry.width}
                        height={ROW_HEIGHT}
                        fill={row.color}
                        fillOpacity={0.65}
                        stroke='currentColor'
                        strokeWidth={1}
                        rx={2}
                        ry={2}
                      />
                    </g>
                    {entry.showLabel && !entry.hasHiddenFollower ? (
                      <g className='text-accent-foreground'>
                        <text
                          x={entry.x + 4}
                          y={y + 1 + ROW_HEIGHT / 2}
                          fill='currentColor'
                          fontSize={12}
                          dominantBaseline='middle'
                        >
                          {entry.label}
                        </text>
                      </g>
                    ) : null}
                  </g>
                )
              })}
              <g className='text-accent-foreground'>
                {rowEntries.map((entry) =>
                  entry.showLabel && entry.hasHiddenFollower ? (
                    <text
                      key={`label-${entry.key}`}
                      x={entry.x + 4}
                      y={y + 1 + ROW_HEIGHT / 2}
                      fill='currentColor'
                      fontSize={12}
                      dominantBaseline='middle'
                    >
                      {entry.label}
                    </text>
                  ) : null,
                )}
              </g>
            </g>
          )
        })}

        {selectionPixels && !disableZoomInteraction && (
          <TimelineDragSelectionOverlay
            startX={selectionPixels.startX}
            endX={selectionPixels.endX}
            top={rowsTop}
            height={rowsHeight}
          />
        )}

        {enableAddCastInteraction ? (
          <rect
            x={0}
            y={rowsTop}
            width={timelineWidth}
            height={rowsHeight}
            fill='transparent'
            className={addHoverBlocked ? '' : 'cursor-copy'}
            onPointerMove={onAddSurfacePointerMove}
            onPointerLeave={onAddSurfacePointerLeave}
            onClick={onAddSurfaceClick}
          />
        ) : null}

        <g className='text-muted-foreground'>
          {minuteTickLabelTimes.map((tickAt) => {
            const x = toX(tickAt)
            return (
              <text
                key={`minute-tick-label-${tickAt}`}
                x={x}
                y={tickLabelY}
                fill='currentColor'
                fontSize={12}
                textAnchor='middle'
              >
                {formatTimelineTimestamp(tickAt)}
              </text>
            )
          })}
        </g>

        <g>
          {orderedCastMarkers.map((marker) => {
            if (
              marker.at < visibleRange.start ||
              marker.at > visibleRange.end
            ) {
              return null
            }
            const x = toX(marker.at)
            const markerLabelWidth = marker.label.length * 6 + 8
            return (
              <g key={marker.id}>
                <line
                  x1={x}
                  x2={x}
                  y1={rowsTop}
                  y2={rowsTop + rowsHeight}
                  stroke={
                    marker.ignored
                      ? 'var(--color-muted-foreground)'
                      : 'var(--color-info-foreground)'
                  }
                  strokeWidth={3}
                  opacity={marker.ignored ? 0.7 : 1}
                />
                <rect
                  x={x - 6}
                  y={rowsTop}
                  width={12}
                  height={rowsHeight}
                  fill='transparent'
                  className='cursor-ew-resize'
                  onPointerDown={marker.onPointerDown}
                  onClick={marker.onClick}
                  onContextMenu={marker.onContextMenu}
                />
                {marker.disableTooltip ? (
                  <rect
                    x={x - markerLabelWidth / 2}
                    y={rowsTop + rowsHeight + 2.5}
                    width={markerLabelWidth}
                    height={14}
                    fill={
                      marker.ignored
                        ? 'var(--color-muted)'
                        : 'var(--color-background)'
                    }
                    stroke={
                      marker.ignored
                        ? 'var(--color-muted-foreground)'
                        : 'var(--color-info-foreground)'
                    }
                    strokeWidth={1}
                    rx={2}
                    ry={2}
                    className='cursor-ew-resize'
                    onPointerDown={marker.onPointerDown}
                    onClick={marker.onClick}
                    onContextMenu={marker.onContextMenu}
                  />
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <rect
                        x={x - markerLabelWidth / 2}
                        y={rowsTop + rowsHeight + 2.5}
                        width={markerLabelWidth}
                        height={14}
                        fill={
                          marker.ignored
                            ? 'var(--color-muted)'
                            : 'var(--color-background)'
                        }
                        stroke={
                          marker.ignored
                            ? 'var(--color-muted-foreground)'
                            : 'var(--color-info-foreground)'
                        }
                        strokeWidth={1}
                        rx={2}
                        ry={2}
                        className='cursor-ew-resize'
                        onPointerDown={marker.onPointerDown}
                        onClick={marker.onClick}
                        onContextMenu={marker.onContextMenu}
                      />
                    </TooltipTrigger>
                    <TooltipContent className='text-xs text-muted-foreground'>
                      {marker.tooltip}
                    </TooltipContent>
                  </Tooltip>
                )}
                <text
                  x={x}
                  y={rowsTop + rowsHeight + 14}
                  fill={
                    marker.ignored
                      ? 'var(--color-muted-foreground)'
                      : 'var(--color-accent-foreground)'
                  }
                  fontSize={12}
                  textAnchor='middle'
                  className='pointer-events-none'
                >
                  {marker.label}
                </text>
              </g>
            )
          })}
        </g>

        {hoverTime !== null && hoverTime !== undefined && hoverTimestamp && (
          <g className='pointer-events-none'>
            <line
              x1={toX(hoverTime)}
              x2={toX(hoverTime)}
              y1={rowsTop}
              y2={rowsTop + rowsHeight}
              stroke='var(--color-accent-foreground)'
              strokeWidth={1}
            />
            <rect
              x={hoverTimestampX - hoverTimestampWidth / 2}
              y={rowsTop + rowsHeight + 2.5}
              width={hoverTimestampWidth}
              height={14}
              fill='var(--color-background)'
              stroke='var(--color-border)'
              strokeWidth={1}
              rx={3}
              ry={3}
            />
            <text
              x={hoverTimestampX}
              y={rowsTop + rowsHeight + 14}
              fill='var(--color-accent-foreground)'
              fontSize={12}
              textAnchor='middle'
            >
              {hoverTimestamp}
            </text>
          </g>
        )}

        {enableAddCastInteraction &&
        addHoverTime !== null &&
        !addHoverBlocked &&
        addHoverTimestamp !== null ? (
          <g className='pointer-events-none'>
            <line
              x1={toX(addHoverTime)}
              x2={toX(addHoverTime)}
              y1={rowsTop}
              y2={rowsTop + rowsHeight}
              stroke='var(--color-info-foreground)'
              strokeWidth={1}
              strokeDasharray='4 3'
              opacity={0.9}
            />
            <rect
              x={addHoverTimestampX - addHoverTimestampWidth / 2}
              y={rowsTop + rowsHeight + 2.5}
              width={addHoverTimestampWidth}
              height={14}
              fill='var(--color-background)'
              stroke='var(--color-info-foreground)'
              strokeWidth={1}
              rx={3}
              ry={3}
            />
            <text
              x={addHoverTimestampX}
              y={rowsTop + rowsHeight + 14}
              fill='var(--color-accent-foreground)'
              fontSize={12}
              textAnchor='middle'
            >
              {addHoverTimestamp}
            </text>
            <rect
              x={addHoverActionLabelX - addHoverActionLabelWidth / 2}
              y={1}
              width={addHoverActionLabelWidth}
              height={14}
              fill='var(--color-background)'
              stroke='var(--color-info-foreground)'
              strokeWidth={1}
              rx={3}
              ry={3}
            />
            <text
              x={addHoverActionLabelX}
              y={12}
              fill='var(--color-accent-foreground)'
              fontSize={12}
              textAnchor='middle'
            >
              {addHoverActionLabel}
            </text>
          </g>
        ) : null}

        {isZoomed && !disableZoomInteraction && (
          <g className='text-muted-foreground'>
            {startEdgeIsBound ? (
              <text
                x={1}
                y={tickLabelY}
                fill='currentColor'
                fontSize={12}
                textAnchor='start'
              >
                {rangeStartLabel}
              </text>
            ) : (
              <ZoomEdgeControl
                x={1}
                y={edgeControlY}
                width={startControlWidth}
                label={rangeStartLabel}
                onReset={handleResetStartEdge}
              />
            )}
            {endEdgeIsBound ? (
              <text
                x={Math.max(timelineWidth - 1, 0)}
                y={tickLabelY}
                fill='currentColor'
                fontSize={12}
                textAnchor='end'
              >
                {rangeEndLabel}
              </text>
            ) : (
              <ZoomEdgeControl
                x={Math.max(timelineWidth - endControlWidth - 1, 0)}
                y={edgeControlY}
                width={endControlWidth}
                label={rangeEndLabel}
                onReset={handleResetEndEdge}
              />
            )}
          </g>
        )}
      </svg>
    </div>
  )
}

export default TimelineContainer
